import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import useOutletRefreshSignal from '../hooks/useOutletRefreshSignal';
import feather from 'feather-icons';
import Alert from '../components/Alert';
import { SERVER_BASE_URL } from '../config/app.config';
import serverClient from '../api/ServerClient';
import { DeleteToken } from '../auth/Auth';
import Loader from '../components/Loader';
import SuccessToast from '../toast/SuccessToast';
import { useLanguage } from '../context/LanguageContext';
import useRefreshSignal from '../hooks/useRefreshSignal';

function MyAccount() {
    const navigate = useNavigate();
    const refreshSignal = useOutletRefreshSignal();
    const { t } = useLanguage();
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const formRef = useRef(null);
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [showToast, setShowToast] = useState(false);
    const [currency, setCurrency] = useState(null);

    const fetchUser = useCallback(async () => {
        try {
            setLoading(true);
            const response = await serverClient.get('/api/user');
            setUser(response?.data?.user);
            setCurrency(response?.data?.user?.currency);
        } catch (e) {
            if (e.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
                navigate('/login');
            } else {
                setError('somethingWentWrong');
            }
        } finally {
            setLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    useEffect(() => {
        if (user) {
            feather.replace();
        }
    }, [user]);

    useRefreshSignal(refreshSignal, () => {
        fetchUser();
    });

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('onlyJPGPNGWebp');
            return;
        }

        const maxSize = 2 * 1024 * 1024;
        if (file.size > maxSize) {
            setError('fileSizeShouldBeLess2MB');
            return;
        }

        const img = new Image();
        img.onload = async () => {
            if (img.width < 100 || img.height < 100) {
                setError('imageTooSmall');
                return;
            }
            try {
                setLoading(true);
                const formData = new FormData();
                formData.append('profile_image', file);

                const response = await serverClient.post(
                    '/api/user/profile/upload-image',
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                        },
                    }
                );

                setUser(response?.data?.user);
            } catch (e) {
                if (e.response?.status === 401) {
                    DeleteToken();
                    setError('userNotAuthorized');
                    navigate('/login');
                } else {
                    setError('somethingWentWrong');
                }
            } finally {
                setLoading(false);
            }
        };
        img.onerror = () => {
            setError('invalidImageFile');
        };
        img.src = URL.createObjectURL(file);
    };

    const handleProfileFormSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);

        const payload = {
            username: document.getElementById('username').value,
            currency,
            phone: document.getElementById('phone').value,
        };

        try {
            const response = await serverClient.put(`/api/user`, payload);

            if (response.status === 201 || response.status === 200) {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 2500); // Auto-hide toast after 2.5 seconds
            }
        } catch (error) {
            if (error.response?.status === 401) {
                DeleteToken();
                setError('userNotAuthorized');
            } else {
                setError(error.response?.data?.message || 'somethingWentWrong');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCameraClick = () => {
        setError(null);
        fileInputRef.current.click();
    };

    return (
        <div className="my-account-page">
            <Loader loading={loading} />
            <header className="section-t-space">
                <div className="custom-container">
                    <div className="header-panel">
                        <Link to="/profile" className="back-btn">
                            <i className="icon" data-feather="arrow-left"></i>
                        </Link>
                        <h2>{t('myAccount')}</h2>
                    </div>
                </div>
            </header>

            {/* Load failure: no profile to show, but the user must still see why */}
            {!user && error && (
                <section className="section-b-space">
                    <div className="custom-container">
                        <div className="p-4">
                            <Alert alert={'danger'}>{t(error)}</Alert>
                        </div>
                    </div>
                </section>
            )}

            {/* Profile Section */}
            {user && (
                <section className="section-b-space">
                    <div className="custom-container">
                        {error && (
                            <div className="p-4">
                                <Alert alert={'danger'}>{t(error)}</Alert>
                            </div>
                        )}
                        <div className="profile-section">
                            <div className="profile-banner">
                                <div className="profile-image">
                                    <img
                                        className="img-fluid profile-pic"
                                        src={
                                            user.profile_image
                                                ? `${SERVER_BASE_URL}/images/profile_image/${user.profile_image}`
                                                : '/assets/images/person/p0.webp'
                                        }
                                        alt="Profile"
                                        referrerPolicy="no-referrer"
                                    />
                                    <span onClick={handleCameraClick} style={{ cursor: 'pointer' }}>
                                        <i className="camera" data-feather="camera"></i>
                                    </span>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        ref={fileInputRef}
                                        style={{ display: 'none' }}
                                        onChange={handleImageChange}
                                    />
                                </div>
                            </div>
                            <h2>{user.username}</h2>
                            <h5>{user.email}</h5>
                        </div>

                        <form
                            ref={formRef}
                            onSubmit={handleProfileFormSubmit}
                            className="auth-form pt-0 mt-3"
                            target="_blank"
                        >
                            <div className="form-group">
                                <label htmlFor="username" className="form-label">
                                    {t('name')}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="username"
                                    defaultValue={user.username}
                                    placeholder={t('enterName')}
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="phone" className="form-label">
                                    {t('phone')}
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="phone"
                                    defaultValue={user.phone}
                                    placeholder={t('phone')}
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="currency" className="form-label">
                                    {t('currency')}
                                </label>
                                <div className="form-input">
                                    <select
                                        onChange={(e) => setCurrency(e.target.value)}
                                        id="currency"
                                        defaultValue={user.currency}
                                        className="form-select"
                                    >
                                        <option value={'BDT'}>{t('taka')}</option>
                                        <option value={'USD'}>{t('dollar')}</option>
                                    </select>
                                </div>
                            </div>
                        </form>

                        <div className="fixed-footer">
                            <button
                                type="submit"
                                onClick={() => formRef.current?.requestSubmit()}
                                onSubmit={handleProfileFormSubmit}
                                className="btn theme-btn w-100 margin-0"
                            >
                                {t('update')}
                            </button>
                        </div>
                    </div>

                    {showToast && (
                        <SuccessToast
                            message={t('userInfoUpdated')}
                            onClose={() => setShowToast(false)}
                        />
                    )}
                </section>
            )}
        </div>
    );
}

export default MyAccount;
